import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { FormField2, IMultiSelectControlField2, ISelectOption } from 'src/lib';
import {
  AlertDialog,
  DialogResult,
  SelectComponent
} from '../../../../../../../components/analytics';
import { useAppDispatch, useAppSelector } from '../../../../../../../hooks';
import { useFormik } from 'formik';
import { IDynamicFilterFormFields } from '../../../../home/types/scenario-form.model';
import FilterLoadingSkeleton from 'src/components/skeletons/filtersLoadingSkeleton';
import Button from '@mui/material/Button';
import useModuleMetadata from 'src/hooks/use-module-metadata.hook';
import MODULE_TO_PAGE_MAP from '../../../../home/types/simulation-module-to-page-map';
import { updateFilterFieldOptions } from '../../../store/planner-slice';
import { ConfigDiffFinder } from '../../../../common/utils';

const CogsFilter = ({
  filterValue,
  setDefaultFilters,
  handleFilterChange,
  handleFilterApply,
  handleFilterReset,
  areCogsFiltersValid,
  areCogsFiltersChanged,
  isApplyEnabled,
  appliedCogsFilters
}) => {
  const appDispatch = useAppDispatch();
  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const { moduleId, sourceId, scenarioId } = useModuleMetadata({
    module: MODULE_TO_PAGE_MAP.PRICING
  });

  const {
    topCogsFiltersValue,
    topCogsFiltersDefaultValue,
    fetchingLocalFiltersData
  } = useAppSelector((state) => state.plannerV2);

  const {
    filters: filterFields,
    initialValues: filterInitialValues,
    combinations
  } = topCogsFiltersValue;

  const filterFormik = useFormik<IDynamicFilterFormFields>({
    initialValues: filterInitialValues,
    enableReinitialize: true,
    onSubmit(values) {
      console.log(values);
    }
  });

  // updating filters values as per the changed action
  useEffect(() => {
    if (
      ConfigDiffFinder.isObjectDifferent(
        filterFormik.values,
        appliedCogsFilters
      )
    ) {
      handleFilterChange(filterFormik);
    } else {
      setDefaultFilters(filterFormik);
    }
  }, [filterFormik.values]);

  const handleFilterSelectionChange = (
    fieldName: string,
    fieldValues: { label: string; value: string }[]
  ) => {
    const changedFilterIndex = filterFields.findIndex(
      (filter) => filter.name === fieldName
    );
    const lastItemIndex = filterFields.length - 1;
    if (changedFilterIndex > -1 && changedFilterIndex !== lastItemIndex) {
      const precedingFilterValues = {
        ...filterFields
          .slice(0, changedFilterIndex)
          .reduce<{ [filterName: string]: { label: string; value: string }[] }>(
            (previousValue, currentValue) => ({
              ...previousValue,
              ...{ [currentValue.name]: filterFormik.values[currentValue.name] }
            }),
            {}
          ),
        ...{
          [fieldName]: fieldValues
        }
      };
      precedingFilterValues[fieldName] = fieldValues;
      const newCombinations = combinations.filter((filterCombination) => {
        const keys = Object.keys(precedingFilterValues);
        return (
          keys.filter((key) =>
            precedingFilterValues[key]
              .map((val) => val.value)
              .includes(filterCombination[key] as string)
          ).length === keys.length
        );
      });
      const remainingFilterValues: {
        [filterName: string]: { label: string; value: string }[];
      } = {};
      for (
        let index = changedFilterIndex + 1;
        index < filterFields.length;
        index++
      ) {
        const filterField = filterFields[index] as IMultiSelectControlField2;
        const dropdownOptionsForFilter = newCombinations
          .map((filterCombination) => filterCombination[filterField.name])
          .filter((value) => value)
          .filter(
            (value, valueIndex, self) => self.indexOf(value) === valueIndex
          )
          .map((val) => ({ label: val as string, value: val as string }));
        // filterField.values = dropdownOptionsForFilter.slice();
        remainingFilterValues[filterField.name] =
          dropdownOptionsForFilter.slice();
      }
      filterFormik.setValues({
        ...precedingFilterValues,
        ...remainingFilterValues
      });
      appDispatch(updateFilterFieldOptions(remainingFilterValues));
    }
    if (lastItemIndex === changedFilterIndex) {
      const previousValues = filterFormik.values;
      filterFormik.setValues({ ...previousValues, [fieldName]: fieldValues });
    }
  };

  const handleFilterApplyFunc = () => {
    handleFilterApply(filterFormik.values);
  };

  // Reset values on filters to ALL
  const onResetFilters = () => {
    // preparing default filter values for reset
    const defaultFilterValues = filterFields.reduce<{
      [key: string]: ISelectOption[];
    }>(
      (acc, curr) => ({
        ...acc,
        [curr.name]: curr.default_value as ISelectOption[]
      }),
      {}
    );
    // reseting the filter values in slice
    appDispatch(updateFilterFieldOptions(defaultFilterValues));

    // resetting the formik
    filterFormik.setValues(filterInitialValues);

    handleFilterReset();
  };

  const handleConfirmationDialogClose = (result: DialogResult) => {
    if (result === DialogResult.YES) {
      onResetFilters();
    }
    setConfirmationDialogOpen(false);
  };

  const onResetClick = () => {
    setConfirmationDialogOpen(true);
  };

  return (
    <>
      {Object.keys(filterFormik.values || {}).length &&
      !fetchingLocalFiltersData ? (
        <Grid className='filter-container' container alignItems='center'>
          <Grid item container spacing={1} xs justifyContent='stretch'>
            {filterFields.map((control: IMultiSelectControlField2) => (
              <Grid item xs={12 / filterFields.length} key={control.name}>
                <SelectComponent
                  value={filterFormik.values[control.name]}
                  handleChange={(val) =>
                    handleFilterSelectionChange(control.name, val)
                  }
                  isDisabled={!control.is_active}
                  label={control.alias}
                  options={control.values}
                  isMultiSelect
                />
              </Grid>
            ))}
          </Grid>
          <Grid
            pl={1}
            pt={2}
            item
            xs='auto'
            container
            spacing={1}
            justifyContent='center'
          >
            <Grid item xs>
              <Button
                disabled={
                  areCogsFiltersValid ||
                  !areCogsFiltersChanged ||
                  !isApplyEnabled
                }
                onClick={() => handleFilterApplyFunc()}
                type='submit'
                variant='outlined'
              >
                Apply
              </Button>
            </Grid>
            <Grid item xs>
              <Button onClick={onResetClick} type='button' variant='outlined'>
                Reset
              </Button>
            </Grid>
          </Grid>
        </Grid>
      ) : (
        null //FilterLoadingSkeleton(4)
      )}
      <AlertDialog
        closeHandler={handleConfirmationDialogClose}
        isDialogOpen={isConfirmationDialogOpen}
        message='Filters will reset and all changes to the table will be lost. Continue?'
        title='Reset'
      />
    </>
  );
};

export default CogsFilter;
